/**
 * useVisitorTracking.js
 *
 * Hook for tracking and displaying live visitors on the site.
 * Used by Admin and Client Success dashboards.
 */
import { useState, useEffect } from "react";
import { rtdb } from "../firebaseConfig";
import { ref, onValue, off } from "firebase/database";

/**
 * Hook to get live visitors list
 * @returns {{ visitors: Array, loading: boolean, onlineCount: number }}
 */
export function useVisitorTracking() {
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isCurrent = true;
    let dbRef = null;

    // Timeout fallback to prevent infinite loading
    const loadingTimeout = setTimeout(() => {
      if (isCurrent && loading) {
        setLoading(false);
      }
    }, 3000);

    try {
      dbRef = ref(rtdb, "visitors");

      onValue(
        dbRef,
        (snapshot) => {
          if (!isCurrent) return;
          if (snapshot.exists()) {
            const data = snapshot.val();
            const visitorsList = Object.entries(data)
              .map(([id, visitor]) => ({
                id,
                ...visitor,
                lastSeenDate: visitor.lastSeen ? new Date(visitor.lastSeen) : null,
              }))
              .filter((v) => v.isOnline)
              .sort((a, b) => (b.lastSeen || 0) - (a.lastSeen || 0));

            setVisitors(visitorsList);
          } else {
            setVisitors([]);
          }
          setLoading(false);
        },
        (error) => {
          if (!isCurrent) return;
          // Silently handle RTDB not configured
          if (error?.message?.includes("configured correctly") || error?.message?.includes("FIREBASE")) {
            console.warn("Realtime Database not configured. Visitor tracking disabled.");
          } else {
            console.error("Visitor tracking error:", error);
          }
          setVisitors([]);
          setLoading(false);
        }
      );
    } catch (err) {
      console.warn("Failed to setup visitor tracking:", err.message);
      setLoading(false);
    }

    return () => {
      isCurrent = false;
      clearTimeout(loadingTimeout);
      if (dbRef) {
        try {
          off(dbRef);
        } catch (e) {
          // Ignore
        }
      }
    };
  }, []);

  return {
    visitors,
    loading,
    onlineCount: visitors.length,
  };
}

/**
 * Hook to track navigation history for a specific visitor
 * @param {string} visitorId
 * @returns {{ history: Array, loading: boolean }}
 */
export function useVisitorHistory(visitorId) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!visitorId) {
      setLoading(false);
      return;
    }

    let isCurrent = true;
    let dbRef = null;

    try {
      dbRef = ref(rtdb, `visitors/${visitorId}`);

      onValue(
        dbRef,
        (snapshot) => {
          if (!isCurrent) return;
          if (snapshot.exists()) {
            const data = snapshot.val();
            setHistory(data.navigationHistory || [data.page]);
          } else {
            setHistory([]);
          }
          setLoading(false);
        },
        (error) => {
          if (!isCurrent) return;
          console.error("Visitor history error:", error);
          setLoading(false);
        }
      );
    } catch (err) {
      console.warn("Failed to setup visitor history:", err.message);
      setLoading(false);
    }

    return () => {
      isCurrent = false;
      if (dbRef) {
        try {
          off(dbRef);
        } catch (e) {
          // Ignore
        }
      }
    };
  }, [visitorId]);

  return { history, loading };
}

export default useVisitorTracking;
